// Iter201 — Admin: Import → Portfolio reconciliation (backfill for accounts
// whose imported open trades predate the automatic Portfolio sync).
// Dry-run first (read-only, per-account before/after), execute is manual.
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, GitMerge, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

export const ImportReconcileCard = () => {
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);

  const run = async (execute) => {
    if (execute && !window.confirm(
      `Execute reconciliation? This will create ${report?.total_positions ?? "the listed"} Portfolio position(s) across ${report?.accounts_needing_reconcile ?? "the affected"} account(s). Manual positions are never modified.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/import-reconcile/${execute ? "execute" : "dry-run"}`);
      setReport(data);
      toast.success(execute
        ? `Reconciliation executed — ${data.total_positions} position(s) created across ${data.accounts_needing_reconcile} account(s)`
        : `Dry-run complete — ${data.total_positions} position(s) would be created across ${data.accounts_needing_reconcile} account(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconciliation failed");
    } finally { setBusy(false); }
  };

  return (
    <section className="border p-4" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}
      data-testid="import-reconcile-card">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-sm font-bold flex items-center gap-2" style={{ fontFamily: "Chivo, sans-serif" }}>
          <GitMerge className="w-4 h-4 text-blue-400" /> Import → Portfolio Reconciliation
        </h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => run(false)} disabled={busy}
            className="h-7 text-xs rounded-sm border-[#27272A]" data-testid="reconcile-dry-run-btn">
            {busy ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
            Dry-run
          </Button>
          <Button size="sm" onClick={() => run(true)} disabled={busy || !report || !report.dry_run || report.total_positions === 0}
            className="h-7 text-xs rounded-sm bg-white text-black hover:bg-gray-200" data-testid="reconcile-execute-btn">
            <PlayCircle className="w-3 h-3 mr-1" /> Execute
          </Button>
        </div>
      </div>
      <p className="text-[10px] mb-3" style={{ color: "var(--hoip-text-tertiary)" }}>
        Backfills Portfolio positions from imported open trades that predate the automatic import→Portfolio
        sync. Dry-run is read-only and shows per-account before/after counts; Execute is enabled only after a
        dry-run. Manually entered positions are never duplicated or modified.
      </p>
      {report && (
        <div className="space-y-2" data-testid="reconcile-report">
          <div className="text-[10px] font-mono-data" style={{ color: "var(--hoip-text-secondary)" }}>
            {report.dry_run ? "DRY-RUN" : "EXECUTED"} · {report.accounts_scanned} account(s) scanned ·{" "}
            {report.accounts_needing_reconcile} needing reconcile · {report.total_positions} position(s){" "}
            {report.dry_run ? "would be created" : "created"}
          </div>
          <div className="border rounded-sm overflow-x-auto" style={{ borderColor: "var(--hoip-border)" }}>
            <table className="w-full dense-table">
              <thead>
                <tr className="border-b" style={{ borderColor: "var(--hoip-border)" }}>
                  {["Account", "Open imported", "Already linked", "Manual match (skipped)", "Unsupported",
                    report.dry_run ? "To create" : "Created", "Stock before→after", "Options before→after"].map(h => (
                    <th key={h} className="text-left text-[9px] font-medium px-2 py-1" style={{ color: "var(--hoip-text-tertiary)" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {report.reports.map(r => (
                  <tr key={r.user_id} className="border-b" style={{ borderColor: "var(--hoip-divider)" }}
                    data-testid={`reconcile-row-${r.user_id}`}>
                    <td className="text-[10px] px-2 py-1">{r.account}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.open_imported}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.already_linked}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.skipped_manual_match}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.unsupported}</td>
                    <td className="text-[10px] font-mono-data px-2 text-emerald-500">{r.created}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.before.stock} → {r.after.stock}</td>
                    <td className="text-[10px] font-mono-data px-2">{r.before.options} → {r.after.options}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {report.reports.some(r => r.details?.length > 0) && (
            <details className="text-[10px]">
              <summary className="cursor-pointer" style={{ color: "var(--hoip-text-tertiary)" }}>Per-trade detail</summary>
              <div className="mt-1 max-h-48 overflow-y-auto font-mono-data space-y-0.5" style={{ color: "var(--hoip-text-secondary)" }}>
                {report.reports.flatMap(r => (r.details || []).map((d, i) => (
                  <div key={`${r.user_id}-${i}`}>
                    {d.ticker} · {d.strategy} · qty {d.qty}{d.strike ? ` · $${d.strike}` : ""}{d.expiry ? ` · ${d.expiry}` : ""} → <span style={{ color: d.action.startsWith("create") ? "#10B981" : "#F59E0B" }}>{d.action}</span>
                  </div>
                )))}
              </div>
            </details>
          )}
        </div>
      )}
    </section>
  );
};
